import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError, materialSaveError } from '../utils';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Button from 'react-bootstrap/Button';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
  adjustScrap,
  adjustStock,
  stockQuantitiesBody,
} from '../utils/stockAdjustments';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };

    default:
      return state;
  }
};

export default function CardboardBoxEditScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { id: cardboardBoxId } = params;

  const [{ loading, error, loadingUpdate }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const [size, setSize] = useState('');
  const [length, setLength] = useState(0);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [weight, setWeight] = useState(0);
  const [stockQuantity, setStockQuantity] = useState(0);
  const [originalStockQuantity, setOriginalStockQuantity] = useState(0);
  const [stockAdjustment, setStockAdjustment] = useState(0);
  const [reservedQuantity, setReservedQuantity] = useState(0);
  const [originalReservedQuantity, setOriginalReservedQuantity] = useState(0);
  const [scrapQuantity, setScrapQuantity] = useState(0);
  const [originalScrapQuantity, setOriginalScrapQuantity] = useState(0);
  const [scrapAdjustment, setScrapAdjustment] = useState(0);
  const [soldQuantity, setSoldQuantity] = useState(0);
  const [originalSoldQuantity, setOriginalSoldQuantity] = useState(0);
  const [manufacturer, setManufacturer] = useState('');
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await api.get(
          `/api/cardboard-boxes/${cardboardBoxId}`
        );
        setSize(data.size);
        setLength(data.length);
        setWidth(data.width);
        setHeight(data.height);
        setWeight(data.weight);
        setStockQuantity(data.stockQuantity);
        setOriginalStockQuantity(data.stockQuantity);
        setReservedQuantity(data.reservedQuantity);
        setOriginalReservedQuantity(data.reservedQuantity);
        setScrapQuantity(data.scrapQuantity);
        setOriginalScrapQuantity(data.scrapQuantity);
        setSoldQuantity(data.soldQuantity);
        setOriginalSoldQuantity(data.soldQuantity);
        setManufacturer(data.manufacturer);
        setIsActive(data.isActive);

        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData();
  }, [cardboardBoxId]);

  const submitHandler = async (e) => {
    e.preventDefault(); // used, so the page doesn't refresh, when the submit button is clicked
    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      await api.put(`/api/cardboard-boxes/${cardboardBoxId}`, {
        size: size,
        length: length,
        width: width,
        height: height,
        weight,
        ...stockQuantitiesBody({
          stockQuantity,
          originalStockQuantity,
          reservedQuantity,
          originalReservedQuantity,
          scrapQuantity,
          originalScrapQuantity,
          soldQuantity,
          originalSoldQuantity,
        }), // the quantities beside the values the screen loaded, which the route compares before it writes
        manufacturer: manufacturer,
        isActive: isActive,
      });
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      toast.success('Cardboard Box gespeichert');
      navigate('/admin/stock');
    } catch (err) {
      toast.error(materialSaveError(err)); // the 409 of a document changed meanwhile, else what the request says
      dispatch({ type: 'UPDATE_FAIL' });
    }
  };

  const handleWheel = (e) => {
    e.target.blur(); // Unfocus the input to prevent scrolling from changing the value
    e.preventDefault(); // Prevent the default scroll behavior
    setTimeout(() => e.target.focus(), 0); // Refocus it after the wheel event
  };

  const handleScrapAdjustment = () => {
    const next = adjustScrap(
      { stockQuantity, reservedQuantity, scrapQuantity },
      scrapAdjustment
    );
    if (next.error) toast.error(next.error);
    setScrapQuantity(next.scrapQuantity);
    setStockQuantity(next.stockQuantity);
    setScrapAdjustment(next.scrapAdjustment);
  };

  const handleStockAdjustment = () => {
    const next = adjustStock(
      { stockQuantity, reservedQuantity },
      stockAdjustment
    );
    if (next.error) toast.error(next.error);
    setStockQuantity(next.stockQuantity);
    setStockAdjustment(next.stockAdjustment);
  };

  return (
    <Container>
      <Container className="small-container">
        <title>Cardboard Box bearbeiten</title>

        <h1 className="screen-heading">Cardboard Box bearbeiten</h1>
        {loading ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group className="mb-3" controlId="size">
              <Form.Label>Size</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={size}
                onChange={(e) => setSize(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="length">
              <Form.Label className="d-flex">
                Length (mm)
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bezieht sich auf die Länge im Inneren der Box
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="number"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                onWheel={handleWheel}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="width">
              <Form.Label className="d-flex">
                Width (mm)
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bezieht sich auf die Tiefe im Inneren der Box
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="number"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                onWheel={handleWheel}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="height">
              <Form.Label className="d-flex">
                Height (mm)
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bezieht sich auf die Höhe im Inneren der Box
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                onWheel={handleWheel}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="weight">
              <Form.Label className="d-flex">
                Weight (g)
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bezieht sich auf das Gesamtgewicht des Kartons (Boden &
                        Deckel)
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <Form.Control
                className="mobile-device-compatible"
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                onWheel={handleWheel}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="stock">
              <Form.Label className="d-flex">
                Stock
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover id="popover-password">
                      <Popover.Body>
                        Die Anzahl an Cardboard Boxes, die sich aktuell im Lager
                        befinden, einschließlich bereits reservierter Cardboard
                        Boxes. (stockQuantity - reservedQuantity = [quantity
                        that is not sold yet])
                        <br />
                        <br />
                        Der Stock kann nicht kleiner sein als Reserved, da die
                        reservedQuantity Teil der stockQuantity ist.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path
                        d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
                l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
                1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
                -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
                1 1 0 0 1 2 0z"
                      />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '15px',
                }}
              >
                <p style={{ margin: '0px 10px 0px 20px' }}>
                  <span>{stockQuantity}</span> pieces
                </p>

                <div className="d-flex">
                  {/* Adjustment field for the user to enter +/- changes */}
                  <Form.Control
                    className="mobile-device-compatible"
                    type="number"
                    value={stockAdjustment}
                    onChange={(e) => setStockAdjustment(e.target.value)}
                    onWheel={handleWheel}
                  />

                  {/* A button that applies the above logic */}
                  <Button variant="secondary" onClick={handleStockAdjustment}>
                    {stockAdjustment >= 0 ? 'Add' : 'Subtract'}
                  </Button>
                </div>
              </div>
            </Form.Group>

            <Form.Group className="mb-3" controlId="reserved">
              <Form.Label className="d-flex">
                Reserved
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover id="popover-password">
                      <Popover.Body>
                        Die Menge an Cardboard Boxes, die bereits für
                        aufgegebene Bestellungen reserviert sind (stockQuantity
                        - reservedQuantity = [quantity that is not sold yet])
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <p style={{ margin: '0px 10px 0px 20px', fontSize: '15px' }}>
                <span>{reservedQuantity}</span> pieces
              </p>
            </Form.Group>

            <Form.Group className="mb-3" controlId="scrap">
              <Form.Label className="d-flex">
                Scrap
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover id="popover-password">
                      <Popover.Body>
                        <strong>
                          Die Anzahl der Cardboard Boxes, die während der
                          Produktion unbrauchbar geworden sind.
                        </strong>
                        <br />
                        <br />
                        Beim Hinzufügen zu **Scrap** kann ausschließlich der
                        freie Lagerbestand (Stock − Reserved) reduziert werden.
                        Übersteigt die hinzugefügte Menge den freien
                        Lagerbestand, wird die Änderung nicht vorgenommen.
                        Reservierungen bleiben dabei immer unangetastet.
                        <br />
                        <br />
                        Wenn eine Menge zu den Scraps hinzugefügt wird, wird
                        dieselbe Menge vom Stock abgezogen.
                        <br />
                        <br />
                        Umgekehrt wird beim Reduzieren der Scrap-Menge dieselbe
                        Menge wieder dem Stock hinzugefügt.
                        <br />
                        <br />
                        Falls nicht genügend Stock verfügbar ist, kann die
                        Anpassung nicht vorgenommen werden.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '15px',
                }}
              >
                <p style={{ margin: '0px 10px 0px 20px' }}>
                  <span style={{ color: 'red' }}>{scrapQuantity}</span> pieces
                </p>

                <div className="d-flex">
                  {/* The numeric field where the user enters the +/- adjustment */}
                  <Form.Control
                    className="mobile-device-compatible"
                    type="number"
                    value={scrapAdjustment}
                    onChange={(e) => setScrapAdjustment(e.target.value)}
                    onWheel={handleWheel}
                  />

                  {/* A button that applies the above logic */}
                  <Button variant="secondary" onClick={handleScrapAdjustment}>
                    {scrapAdjustment >= 0 ? 'Add' : 'Subtract'}
                  </Button>
                </div>
              </div>
            </Form.Group>

            <Form.Group className="mb-3" controlId="sold">
              <Form.Label className="d-flex">Sold</Form.Label>
              <p style={{ margin: '0px 10px 0px 20px', fontSize: '15px' }}>
                <span style={{ color: 'green' }}>{soldQuantity}</span> pieces
              </p>
            </Form.Group>

            <Form.Group className="mb-3" controlId="manufacturer">
              <Form.Label>Manufacturer</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="isActive">
              <Form.Label className="d-flex">
                Active
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover>
                      <Popover.Body>
                        Bestimmt, ob diese Cardboard Box für die berechnung der
                        stockQuantities der Layout documents mit einbezogen
                        werden soll.
                        <br />
                        <br />
                        Wenn nein, dann sind alle Layouts die diese Cardboard
                        Box verwenden als ausverkauft markiert.
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>

            <div className="d-flex justify-content-center align-items-center mt-5">
              <Button
                variant="secondary"
                className="mx-3"
                disabled={loadingUpdate}
                type="submit"
              >
                {/* disabled prop is set to loadingUpdate, because it should not be possible, to click the update button multiple times */}
                Speichern
              </Button>

              {loadingUpdate && (
                <div className="overlay">
                  <LoadingLogo />
                </div>
              )}
            </div>
          </Form>
        )}
      </Container>
    </Container>
  );
}
